import React, { useState, useEffect } from 'react'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  CalendarDaysIcon,
  SparklesIcon
} from '@heroicons/react/24/outline'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import { DateHelper } from '../utils/dateHelper'

interface Schedule {
  id: string
  date: string
  startTime: string
  endTime: string
  status: 'available' | 'booked' | 'blocked'
}

interface BookingCalendarProps {
  teacherId: string
  onTimeSlotSelect: (schedule: Schedule, startTime: string, endTime: string) => void
  selectedDuration: number
}

const BookingCalendar: React.FC<BookingCalendarProps> = ({
  teacherId,
  onTimeSlotSelect,
  selectedDuration
}) => {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [schedules, setSchedules] = useState<Schedule[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null)

  // 获取当前月份的天数
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const days = []

    // 添加上月末尾的天数（填充）
    const firstDayOfWeek = firstDay.getDay()
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const prevDate = new Date(year, month, -i)
      days.push({ date: prevDate, isCurrentMonth: false })
    }

    // 添加当月的天数
    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push({ date: new Date(year, month, day), isCurrentMonth: true })
    }

    // 添加下月开始的天数（填充）
    const remainingDays = 42 - days.length // 6周 x 7天
    for (let day = 1; day <= remainingDays; day++) {
      const nextDate = new Date(year, month + 1, day)
      days.push({ date: nextDate, isCurrentMonth: false })
    }

    return days
  }

  // 获取时间安排
  const fetchSchedules = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
      const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)

      const response = await fetch(
        `/api/schedules/teacher/${teacherId}/available?startDate=${DateHelper.formatDateForInput(
          startDate
        )}&endDate=${DateHelper.formatDateForInput(endDate)}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      )

      if (response.ok) {
        const data = await response.json()
        setSchedules(data)
      }
    } catch (error) {
      console.error('获取时间安排失败:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (teacherId) {
      fetchSchedules()
    }
  }, [teacherId, currentDate])

  // 生成时间段
  const generateTimeSlots = (schedule: Schedule) => {
    const slots = []
    const start =
      parseInt(schedule.startTime.split(':')[0]) * 60 + parseInt(schedule.startTime.split(':')[1])
    const end =
      parseInt(schedule.endTime.split(':')[0]) * 60 + parseInt(schedule.endTime.split(':')[1])
    const duration = selectedDuration * 60 // 转换为分钟

    for (let time = start; time + duration <= end; time += 30) {
      // 每30分钟一个时间段
      const startHour = Math.floor(time / 60)
      const startMin = time % 60
      const endTime = time + duration
      const endHour = Math.floor(endTime / 60)
      const endMin = endTime % 60

      const startTimeStr = `${startHour.toString().padStart(2, '0')}:${startMin
        .toString()
        .padStart(2, '0')}`
      const endTimeStr = `${endHour.toString().padStart(2, '0')}:${endMin
        .toString()
        .padStart(2, '0')}`

      slots.push({
        schedule,
        startTime: startTimeStr,
        endTime: endTimeStr,
        display: `${startTimeStr} - ${endTimeStr}`,
        id: `${schedule.id}-${startTimeStr}`
      })
    }

    return slots
  }

  // 获取某天的时间安排
  const getSchedulesForDate = (date: Date) => {
    const dateStr = DateHelper.formatDateForInput(date)
    return schedules.filter(
      (schedule) => DateHelper.isSameDay(schedule.date, dateStr) && schedule.status === 'available'
    )
  }

  // 获取某天的所有可用时间段
  const getAllTimeSlotsForDate = (date: Date) => {
    const dailySchedules = getSchedulesForDate(date)
    return dailySchedules.flatMap((schedule) => generateTimeSlots(schedule))
  }

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))
    setSelectedDate(null)
    setSelectedSlot(null)
  }

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))
    setSelectedDate(null)
    setSelectedSlot(null)
  }

  const days = getDaysInMonth(currentDate)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const weekDays = ['日', '一', '二', '三', '四', '五', '六']

  // 选中的日期的可用时间段
  const selectedDateTimeSlots = selectedDate ? getAllTimeSlotsForDate(selectedDate) : []

  const handleSlotSelect = (slot: any) => {
    setSelectedSlot(slot.id)
    onTimeSlotSelect(slot.schedule, slot.startTime, slot.endTime)
  }

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* 左侧日历 */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 flex-1">
        {/* 日历头部 */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center">
              <CalendarDaysIcon className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">选择日期</h3>
              <p className="text-xs text-slate-500">点击日期查看可用时段</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={prevMonth}
              className="p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-500 hover:text-slate-700"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <span className="text-base font-semibold text-slate-900 min-w-[120px] text-center">
              {currentDate.getFullYear()}年{currentDate.getMonth() + 1}月
            </span>
            <button
              onClick={nextMonth}
              className="p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-500 hover:text-slate-700"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {loading && (
          <div className="flex justify-center py-12">
            <div className="relative">
              <div className="w-10 h-10 border-3 border-indigo-100 border-t-indigo-600 rounded-full animate-spin" />
              <SparklesIcon className="w-4 h-4 text-indigo-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            </div>
          </div>
        )}

        {!loading && (
          <>
            {/* 星期标题 */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {weekDays.map((day, index) => (
                <div
                  key={day}
                  className={`p-2 text-center text-xs font-semibold ${
                    index === 0 || index === 6 ? 'text-rose-500' : 'text-slate-500'
                  }`}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* 日期格子 */}
            <div className="grid grid-cols-7 gap-1">
              {days.map((day, index) => {
                const daySchedules = getSchedulesForDate(day.date)
                const hasSlots = daySchedules.length > 0
                const isToday = day.date.getTime() === today.getTime()
                const isSelected = selectedDate?.getTime() === day.date.getTime()
                const isPast = day.date < today
                const isDisabled = !day.isCurrentMonth || isPast || !hasSlots
                const isWeekend = day.date.getDay() === 0 || day.date.getDay() === 6

                return (
                  <button
                    key={index}
                    disabled={isDisabled}
                    onClick={() => {
                      setSelectedDate(day.date)
                      setSelectedSlot(null)
                    }}
                    className={`
                      relative h-12 rounded-xl transition-all duration-200
                      ${!day.isCurrentMonth ? 'invisible' : ''}
                      ${
                        isDisabled
                          ? 'bg-slate-50 text-slate-300 cursor-not-allowed'
                          : isSelected
                          ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-200/50 scale-105'
                          : 'bg-white hover:bg-indigo-50 text-slate-700 hover:text-indigo-600 border border-slate-100 hover:border-indigo-200'
                      }
                    `}
                  >
                    <div className="flex flex-col items-center justify-center h-full">
                      <span
                        className={`text-sm font-medium ${
                          isToday && !isSelected
                            ? 'text-indigo-600 font-bold'
                            : isWeekend && !isSelected && !isDisabled
                            ? 'text-rose-500'
                            : ''
                        }`}
                      >
                        {day.date.getDate()}
                      </span>
                      {hasSlots && !isDisabled && (
                        <div
                          className={`mt-0.5 w-1.5 h-1.5 rounded-full ${
                            isSelected ? 'bg-white' : 'bg-emerald-500'
                          }`}
                        />
                      )}
                    </div>
                    {isToday && !isSelected && (
                      <div className="absolute bottom-1 left-1/2 -translate-x-1/2 text-[8px] font-bold text-indigo-500">
                        今天
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
          </>
        )}

        {/* 图例 */}
        <div className="flex items-center justify-center gap-6 mt-6 pt-4 border-t border-slate-100">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <div className="w-2 h-2 bg-emerald-500 rounded-full" />
            <span>可预约</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <div className="w-2 h-2 bg-slate-300 rounded-full" />
            <span>不可预约</span>
          </div>
        </div>
      </div>

      {/* 右侧时间选择 */}
      <div
        className={`bg-white rounded-2xl border border-slate-100 shadow-sm p-6 flex-1 transition-all duration-300 ${
          selectedDate ? 'opacity-100' : 'opacity-50 pointer-events-none'
        }`}
      >
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-xl flex items-center justify-center">
            <ClockIcon className="h-5 w-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">
              {selectedDate
                ? `${selectedDate.getMonth() + 1}月${selectedDate.getDate()}日 可选时间`
                : '请先选择日期'}
            </h3>
            <p className="text-xs text-slate-500">
              课程时长：<span className="font-semibold text-indigo-600">{selectedDuration}小时</span>
            </p>
          </div>
        </div>

        <div className="max-h-[360px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
          {selectedDateTimeSlots.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {selectedDateTimeSlots.map((slot) => {
                const isSlotSelected = selectedSlot === slot.id

                return (
                  <button
                    key={slot.id}
                    onClick={() => handleSlotSelect(slot)}
                    className={`
                      relative px-3 py-3 text-sm rounded-xl border transition-all duration-200
                      ${
                        isSlotSelected
                          ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white border-transparent shadow-lg shadow-indigo-200/50'
                          : 'bg-indigo-50 text-indigo-700 border-indigo-100 hover:bg-indigo-100 hover:border-indigo-200'
                      }
                    `}
                  >
                    <span className="font-semibold">{slot.startTime}</span>
                    {isSlotSelected && (
                      <CheckCircleIcon className="absolute top-1 right-1 w-4 h-4 text-white" />
                    )}
                  </button>
                )
              })}
            </div>
          ) : selectedDate ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ClockIcon className="w-8 h-8 text-slate-400" />
              </div>
              <h4 className="text-base font-semibold text-slate-800 mb-1">该日期暂无可预约时间</h4>
              <p className="text-sm text-slate-500">
                可能是时段不足 {selectedDuration} 小时，请选择其他日期
              </p>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <CalendarDaysIcon className="w-8 h-8 text-indigo-400" />
              </div>
              <h4 className="text-base font-semibold text-slate-800 mb-1">请在左侧选择日期</h4>
              <p className="text-sm text-slate-500">选择后将显示可用时间段</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default BookingCalendar
